// packages/web/src/components/layout/layouts/SuperAdminLayout.tsx
// FAANG-GRADE: Dedicated layout for platform super administrators
// Platform governance and system administration focus

'use client';

import { Box, IconButton, Avatar, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { apiFetch } from '../../../lib/api';
import { logout } from '../../../lib/auth/logout';
import { RootState } from '../../../store';
import BottomNavigation from '../BottomNavigation';
import DeploymentIndicator from '../DeploymentIndicator';
import HeaderBranding from '../HeaderBranding';
import { BaseHeader } from '../headers/BaseHeader';
import { useAuthContext } from '../providers/AuthProvider';

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

export const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const router = useRouter();
  const { user, role } = useAuthContext();


  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    console.log('[SuperAdminLayout] Profile menu open clicked');
    setAnchorEl(event.currentTarget);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(async () => {
    handleProfileMenuClose();
    // FAANG-GRADE: Use unified logout function - single source of truth
    await logout();
  }, [handleProfileMenuClose]);

  const handleProfileClick = useCallback(() => {
    handleProfileMenuClose();
    router.push('/super-admin/settings');
  }, [handleProfileMenuClose, router]);

  const branding = useMemo(() => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      transition: 'all 0.2s ease'
    }}>
      <HeaderBranding
        size="large"
        clickable={true}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
        </Typography>
        <Typography
          sx={{
            color: 'error.main',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          Platform Administration
        </Typography>
      </Box>
    </Box>
  ), []);

  // Memoize avatar letter to prevent unnecessary re-renders
  const avatarLetter = useMemo(() => (
    (user as any)?.firstName?.charAt(0) ||
    (user as any)?.first_name?.charAt(0) ||
    user?.email?.charAt(0) ||
    ''
  ), [user]);

  const actions = useMemo(() => (
    <IconButton
      id="user-menu-button"
      edge="end"
      aria-label="User menu"
      aria-haspopup="menu"
      aria-controls={anchorEl ? 'user-menu' : undefined}
      aria-expanded={anchorEl ? 'true' : undefined}
      data-testid="user-menu-button"
      onClick={handleProfileMenuOpen}
      color="inherit"
    >
      <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.error.main }}>
        {avatarLetter}
      </Avatar>
    </IconButton>
  ), [handleProfileMenuOpen, theme.palette.error.main, avatarLetter]);

  return (
    <Box sx={{ display: 'flex' }}>
      <BaseHeader
        branding={branding}
        actions={actions}
      >
        <></>
      </BaseHeader>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          pb: 7 // Bottom padding for navigation
        }}
      >
        {/* Toolbar spacer for fixed header */}
        <Box sx={{ minHeight: '64px' }} />

        {children}
      </Box>

      <BottomNavigation
        role={role}
        activePath={router.pathname}
        onNavigate={(path) => router.push(path)}
      />

      {/* <AskTechaddsWidget /> - Disabled for super admin pages */}
      <DeploymentIndicator />

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        MenuListProps={{ 'aria-labelledby': 'user-menu-button' }}
      >
        <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
        <MenuItem onClick={handleLogout} data-testid="user-menu-logout">Logout</MenuItem>
      </Menu>
    </Box>
  );
};